"use client";

import { useState } from "react";
import { useToolUi } from "@/components/tools/tool-ui";
import { writeToClipboard } from "@/lib/clipboard";
import { sampleWords, uniqueWords } from "@/lib/tool-kit/random-tools/words";

type WordType = "noun" | "verb" | "adjective" | "any";

const WORD_TYPE_VALUES: WordType[] = ["noun", "verb", "adjective", "any"];

/** English strings. A translated content file overrides these through `ui`. */
export const RANDOM_WORD_GENERATOR_STRINGS = {
  wordTypeLabels: {
    noun: "Nouns",
    verb: "Verbs",
    adjective: "Adjectives",
    any: "Any word",
  },
  wordType: "Word type",
  howManyWords: "How many words (1–20)",
  generateWords: "Generate Words",
  copied: "Copied!",
  copyAll: "Copy All",
  yourRandomWords: "Your random words",
} as const;

const NOUNS: string[] = [
  "mountain",
  "river",
  "ocean",
  "forest",
  "garden",
  "bridge",
  "castle",
  "village",
  "harbor",
  "island",
  "lantern",
  "mirror",
  "compass",
  "journey",
  "ladder",
  "window",
  "blanket",
  "bicycle",
  "library",
  "kitchen",
  "thunder",
  "shadow",
  "whisper",
  "morning",
  "autumn",
  "winter",
  "spring",
  "summer",
  "teacher",
  "doctor",
  "artist",
  "farmer",
  "captain",
  "neighbor",
  "stranger",
  "friend",
  "family",
  "orchard",
  "meadow",
  "valley",
  "desert",
  "glacier",
  "volcano",
  "canyon",
  "pebble",
  "boulder",
  "feather",
  "wing",
  "nest",
  "branch",
  "root",
  "blossom",
  "seed",
  "harvest",
  "basket",
  "bottle",
  "candle",
  "clock",
  "engine",
  "hammer",
  "needle",
  "thread",
  "pocket",
  "ribbon",
  "saddle",
  "telescope",
  "umbrella",
  "wagon",
  "anchor",
  "barrel",
  "cabin",
  "canoe",
  "cottage",
  "ferry",
  "gate",
  "hallway",
  "jacket",
  "kettle",
  "market",
  "melody",
  "museum",
  "notebook",
  "orchestra",
  "package",
  "painting",
  "parade",
  "pattern",
  "picnic",
  "pillow",
  "planet",
  "puzzle",
  "quilt",
  "rainbow",
  "recipe",
  "riddle",
  "rocket",
  "sandwich",
  "scissors",
  "sculpture",
  "shelf",
  "shore",
  "signal",
  "silence",
  "sketch",
  "snowflake",
  "song",
  "spark",
  "statue",
  "storm",
  "story",
  "sunrise",
  "sunset",
  "theater",
  "ticket",
  "tower",
  "trail",
  "treasure",
  "tunnel",
  "voyage",
  "waterfall",
  "wilderness",
];

const VERBS: string[] = [
  "run",
  "jump",
  "swim",
  "climb",
  "build",
  "carry",
  "catch",
  "dance",
  "discover",
  "draw",
  "dream",
  "drift",
  "explore",
  "fold",
  "gather",
  "glow",
  "grow",
  "imagine",
  "invent",
  "launch",
  "listen",
  "measure",
  "mix",
  "paint",
  "plant",
  "polish",
  "pour",
  "practice",
  "reach",
  "remember",
  "rescue",
  "rush",
  "sail",
  "scatter",
  "search",
  "shape",
  "share",
  "shine",
  "sketch",
  "sleep",
  "slide",
  "sprint",
  "stack",
  "stretch",
  "study",
  "swing",
  "teach",
  "think",
  "throw",
  "travel",
  "twist",
  "wander",
  "wave",
  "weave",
  "whisper",
  "whistle",
  "wonder",
  "write",
  "arrange",
  "balance",
  "bloom",
  "bounce",
  "breathe",
  "brew",
  "celebrate",
  "chase",
  "collect",
  "compose",
  "cook",
  "create",
  "deliver",
  "design",
  "dig",
  "earn",
  "echo",
  "edit",
  "examine",
  "fetch",
  "float",
  "follow",
];

const ADJECTIVES: string[] = [
  "ancient",
  "bold",
  "brave",
  "bright",
  "brilliant",
  "calm",
  "careful",
  "cheerful",
  "clever",
  "cold",
  "colorful",
  "cozy",
  "curious",
  "daring",
  "deep",
  "delicate",
  "distant",
  "eager",
  "early",
  "elegant",
  "empty",
  "enormous",
  "fearless",
  "fierce",
  "fragile",
  "fresh",
  "friendly",
  "gentle",
  "gigantic",
  "gleaming",
  "golden",
  "graceful",
  "grand",
  "happy",
  "heavy",
  "hidden",
  "hollow",
  "honest",
  "humble",
  "hungry",
  "icy",
  "invisible",
  "jolly",
  "keen",
  "kind",
  "lively",
  "lonely",
  "loud",
  "loyal",
  "lucky",
  "mellow",
  "mighty",
  "modern",
  "mysterious",
  "narrow",
  "noble",
  "odd",
  "patient",
  "peaceful",
  "playful",
  "polished",
  "proud",
  "quick",
  "quiet",
  "rare",
  "rustic",
  "shiny",
  "silent",
  "silver",
  "simple",
  "sleepy",
  "smooth",
  "soft",
  "sparkling",
  "steady",
  "swift",
  "tender",
  "tiny",
  "vast",
  "vivid",
  "warm",
  "wild",
  "wise",
];

// "sketch" and "whisper" are on both the noun and verb lists; "Any word" lists each once.
const ANY_WORDS: string[] = uniqueWords([...NOUNS, ...VERBS, ...ADJECTIVES]);

export const WORD_POOLS: Record<WordType, string[]> = {
  noun: NOUNS,
  verb: VERBS,
  adjective: ADJECTIVES,
  any: ANY_WORDS,
};

const MIN_COUNT = 1;
const MAX_COUNT = 20;

function clampCount(value: number): number {
  if (Number.isNaN(value)) {
    return MIN_COUNT;
  }
  return Math.min(MAX_COUNT, Math.max(MIN_COUNT, Math.floor(value)));
}

export default function RandomWordGeneratorWidget() {
  const strings = useToolUi(RANDOM_WORD_GENERATOR_STRINGS);
  const [wordType, setWordType] = useState<WordType>("noun");
  const [count, setCount] = useState(5);
  const [words, setWords] = useState<string[]>([]);
  const [copied, setCopied] = useState(false);

  function handleGenerate() {
    setWords(sampleWords(WORD_POOLS[wordType], count));
    setCopied(false);
  }

  function handleCopyAll() {
    if (words.length === 0) {
      return;
    }
    writeToClipboard(words.join(", ")).then((copied) => {
      if (!copied) {
        return;
      }

      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  return (
    <>
      {/* Word type */}
      <section className="space-y-3">
        <p className="font-medium text-foreground text-sm">
          {strings.wordType}
        </p>
        <div className="flex flex-wrap gap-3">
          {WORD_TYPE_VALUES.map((t) => (
            <label
              className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5"
              key={t}
            >
              <input
                checked={wordType === t}
                className="accent-primary"
                name="word-type"
                onChange={() => setWordType(t)}
                type="radio"
                value={t}
              />
              {strings.wordTypeLabels[t]}
            </label>
          ))}
        </div>
      </section>

      {/* Count */}
      <div className="space-y-2">
        <label
          className="font-medium text-foreground text-sm"
          htmlFor="word-count"
        >
          {strings.howManyWords}
        </label>
        <input
          className="w-32 rounded-lg border border-border bg-card p-3 text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          id="word-count"
          max={MAX_COUNT}
          min={MIN_COUNT}
          onChange={(e) => setCount(clampCount(Number(e.target.value)))}
          type="number"
          value={count}
        />
      </div>

      {/* Actions */}
      <div className="flex flex-wrap gap-3">
        <button
          className="rounded-lg bg-primary px-5 py-2 font-medium text-primary-foreground text-sm transition-opacity hover:opacity-90"
          onClick={handleGenerate}
          type="button"
        >
          {strings.generateWords}
        </button>
        <button
          className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
          disabled={words.length === 0}
          onClick={handleCopyAll}
          type="button"
        >
          {copied ? strings.copied : strings.copyAll}
        </button>
      </div>

      {/* Results */}
      {words.length > 0 && (
        <section className="space-y-3">
          <h2 className="font-medium text-foreground text-sm">
            {strings.yourRandomWords}
          </h2>
          <div className="flex flex-wrap gap-2">
            {words.map((word) => (
              <span
                className="rounded-full border border-border bg-card px-4 py-1.5 text-foreground text-sm"
                key={word}
              >
                {word}
              </span>
            ))}
          </div>
        </section>
      )}
    </>
  );
}
